import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, ShoppingCart, AlertTriangle, Printer } from 'lucide-react';
import api from '../api/client';

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:8,padding:'8px 12px',fontSize:12}}>
        <p style={{color:'var(--text-secondary)',marginBottom:4}}>{label}</p>
        {payload.map((p: any, i: number) => (
          <p key={i} style={{color:p.color,fontWeight:600}}>₾{Number(p.value).toFixed(2)}</p>
        ))}
      </div>
    );
  }
  return null;
};

export default function FinancialPage() {
  const [year, setYear] = useState(new Date().getFullYear());

  const { data, isLoading } = useQuery({
    queryKey: ['financial', year],
    queryFn: () => api.get(`/stats/financial?year=${year}`),
  } as any);

  const d = (data as any)?.data;

  const monthlyData = (d?.monthly_fines || []).map((m: any) => ({
    name: m.month?.slice(5) || '',
    'დარიცხული': parseFloat(m.charged||0),
    'გადახდილი': parseFloat(m.collected||0),
  }));

  const orderData = (d?.monthly_orders || []).map((m: any) => ({
    name: m.month?.slice(5) || '',
    'დახარჯული': parseFloat(m.spent||0),
  }));

  const handlePrint = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html><head><title>ფინანსური ანგარიში ${year}</title>
      <style>
        body{font-family:sans-serif;padding:24px;max-width:800px;margin:0 auto}
        h1{color:#1d4ed8;border-bottom:2px solid #1d4ed8;padding-bottom:8px}
        h2{color:#374151;font-size:16px;margin:20px 0 10px}
        .grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:20px}
        .card{border:1px solid #e2e8f0;border-radius:8px;padding:14px;text-align:center}
        .card-val{font-size:22px;font-weight:700;color:#1d4ed8}
        .card-label{font-size:11px;color:#94a3b8;margin-top:4px}
        table{width:100%;border-collapse:collapse;font-size:13px}
        th{background:#f8fafc;padding:8px;text-align:left;font-weight:600;border-bottom:2px solid #e2e8f0}
        td{padding:8px;border-bottom:1px solid #f1f5f9}
        .footer{text-align:center;font-size:10px;color:#94a3b8;margin-top:20px;padding-top:12px;border-top:1px solid #e2e8f0}
        @media print{body{padding:8px}}
      </style></head><body>
      <h1>📚 Folios — ფინანსური ანგარიში ${year}</h1>
      <h2>ჯარიმები</h2>
      <div class="grid">
        <div class="card"><div class="card-val">₾${d?.fines?.total?.toFixed(2)||0}</div><div class="card-label">სულ დარიცხული</div></div>
        <div class="card"><div class="card-val" style="color:#16a34a">₾${d?.fines?.paid?.toFixed(2)||0}</div><div class="card-label">გადახდილი</div></div>
        <div class="card"><div class="card-val" style="color:#dc2626">₾${d?.fines?.unpaid?.toFixed(2)||0}</div><div class="card-label">გადაუხდელი</div></div>
      </div>
      <h2>ბიუჯეტი და შესყიდვები</h2>
      <div class="grid">
        <div class="card"><div class="card-val">₾${d?.budget?.total?.toFixed(2)||0}</div><div class="card-label">სულ ბიუჯეტი</div></div>
        <div class="card"><div class="card-val" style="color:#dc2626">₾${d?.budget?.spent?.toFixed(2)||0}</div><div class="card-label">დახარჯული</div></div>
        <div class="card"><div class="card-val" style="color:#16a34a">₾${(((d?.budget?.total||0)-(d?.budget?.spent||0))).toFixed(2)}</div><div class="card-label">დარჩენილი</div></div>
      </div>
      ${d?.top_fined?.length ? `
      <h2>ყველაზე მეტი ჯარიმა</h2>
      <table><tr><th>წევრი</th><th>სულ</th><th>გადახდილი</th></tr>
      ${d.top_fined.map((f: any) => `<tr><td>${f.name}</td><td>₾${parseFloat(f.total).toFixed(2)}</td><td>₾${parseFloat(f.paid).toFixed(2)}</td></tr>`).join('')}
      </table>` : ''}
      <div class="footer">ანგარიში გენერირდა: ${new Date().toLocaleString('ka-GE')} · © 2026 Kakhaber Revazishvili · SANGU</div>
      </body></html>`);
    win.document.close();
    win.print();
  };

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:600,color:'var(--text-primary)',margin:0}}>ფინანსური ანგარიშგება</h1>
          <p style={{fontSize:12,color:'var(--text-faint)',marginTop:4}}>ბიუჯეტი, ჯარიმები, შესყიდვები</p>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            style={{background:'var(--bg-dark2)',border:'1px solid #1e2d45',borderRadius:8,padding:'7px 12px',fontSize:13,color:'var(--text-secondary)',outline:'none',cursor:'pointer'}}>
            {[2026,2025,2024].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={handlePrint}
            style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'var(--bg-dark2)',border:'1px solid #1e2d45',borderRadius:8,color:'var(--accent)',fontSize:12,cursor:'pointer'}}>
            <Printer size={14}/> ბეჭდვა
          </button>
        </div>
      </div>

      {isLoading ? (
        <div style={{textAlign:'center',padding:48,color:'var(--text-faint)'}}>იტვირთება...</div>
      ) : (
        <>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:16}}>
            {[
              {label:'სულ ჯარიმა',val:`₾${d?.fines?.total?.toFixed(2)||'0.00'}`,color:'var(--danger)',icon:AlertTriangle,bg:'var(--danger-bg)'},
              {label:'გადახდილი',val:`₾${d?.fines?.paid?.toFixed(2)||'0.00'}`,color:'var(--success)',icon:TrendingUp,bg:'var(--success-bg)'},
              {label:'გადაუხდელი',val:`₾${d?.fines?.unpaid?.toFixed(2)||'0.00'}`,color:'var(--warning)',icon:TrendingDown,bg:'var(--warning-bg)'},
              {label:'შესყიდვები',val:`₾${d?.orders?.total?.toFixed(2)||'0.00'}`,color:'var(--accent)',icon:ShoppingCart,bg:'var(--accent-bg)'},
            ].map(s => (
              <div key={s.label} style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,padding:16,display:'flex',gap:12,alignItems:'center'}}>
                <div style={{width:36,height:36,borderRadius:10,background:s.bg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <s.icon size={18} style={{color:s.color}} />
                </div>
                <div>
                  <div style={{fontSize:11,color:'var(--text-faint)',marginBottom:3}}>{s.label}</div>
                  <div style={{fontSize:20,fontWeight:700,color:s.color}}>{s.val}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
            <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,padding:16}}>
              <div style={{fontSize:13,fontWeight:500,color:'var(--text-secondary)',marginBottom:16,display:'flex',alignItems:'center',gap:8}}>
                <span style={{width:3,height:14,borderRadius:2,background:'var(--danger)',display:'inline-block'}}></span>
                ჯარიმები თვეების მიხედვით
              </div>
              {monthlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={monthlyData} margin={{top:0,right:0,bottom:0,left:-20}}>
                    <XAxis dataKey="name" tick={{fill:'var(--text-faint)',fontSize:10}} axisLine={false} tickLine={false} />
                    <YAxis tick={{fill:'var(--text-faint)',fontSize:10}} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{fontSize:11,color:'var(--text-muted)'}} />
                    <Bar dataKey="დარიცხული" fill="#ef4444" radius={[3,3,0,0]} />
                    <Bar dataKey="გადახდილი" fill="#10b981" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p style={{textAlign:'center',color:'var(--text-dimmed)',fontSize:12,padding:24}}>მონაცემი არ არის</p>}
            </div>

            <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,padding:16}}>
              <div style={{fontSize:13,fontWeight:500,color:'var(--text-secondary)',marginBottom:16,display:'flex',alignItems:'center',gap:8}}>
                <span style={{width:3,height:14,borderRadius:2,background:'var(--accent)',display:'inline-block'}}></span>
                შესყიდვები თვეების მიხედვით
              </div>
              {orderData.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={orderData} margin={{top:0,right:0,bottom:0,left:-20}}>
                    <XAxis dataKey="name" tick={{fill:'var(--text-faint)',fontSize:10}} axisLine={false} tickLine={false} />
                    <YAxis tick={{fill:'var(--text-faint)',fontSize:10}} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="დახარჯული" fill="#3b82f6" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p style={{textAlign:'center',color:'var(--text-dimmed)',fontSize:12,padding:24}}>მონაცემი არ არის</p>}
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
              <div style={{padding:'12px 16px',borderBottom:'1px solid #1e2d45',fontSize:13,fontWeight:500,color:'var(--text-secondary)'}}>ბიუჯეტების მდგომარეობა</div>
              {(d?.budget?.items||[]).map((b: any) => {
                const pct = Math.min(100, Math.round((b.spent/b.amount)*100));
                return (
                  <div key={b.id} style={{padding:'12px 16px',borderBottom:'1px solid #0f1826'}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                      <span style={{fontSize:13,color:'var(--text-secondary)',fontWeight:500}}>{b.name}</span>
                      <span style={{fontSize:12,color:'var(--warning)',fontWeight:500}}>₾{parseFloat(b.spent).toFixed(2)} / ₾{parseFloat(b.amount).toFixed(2)}</span>
                    </div>
                    <div style={{height:6,background:'var(--border)',borderRadius:3}}>
                      <div style={{height:'100%',width:`${pct}%`,background:pct>80?'#ef4444':'#3b82f6',borderRadius:3,transition:'width 0.3s'}} />
                    </div>
                    <div style={{fontSize:10,color:'var(--text-faint)',marginTop:4}}>{pct}% გამოყენებული · ₾{(b.amount-b.spent).toFixed(2)} დარჩა</div>
                  </div>
                );
              })}
              {(!d?.budget?.items?.length) && <p style={{padding:24,textAlign:'center',color:'var(--text-dimmed)',fontSize:12}}>ბიუჯეტი არ არის</p>}
            </div>

            <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
              <div style={{padding:'12px 16px',borderBottom:'1px solid #1e2d45',fontSize:13,fontWeight:500,color:'var(--text-secondary)'}}>ყველაზე მეტი ჯარიმა</div>
              {(d?.top_fined||[]).length === 0 && <p style={{padding:24,textAlign:'center',color:'var(--text-dimmed)',fontSize:12}}>ჯარიმა არ არის</p>}
              {(d?.top_fined||[]).map((f: any, i: number) => (
                <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 16px',borderBottom:'1px solid #0f1826'}}>
                  <div style={{width:24,height:24,borderRadius:6,background:'var(--bg-active)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:'var(--accent)',flexShrink:0}}>{i+1}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,color:'var(--text-secondary)'}}>{f.name}</div>
                    <div style={{fontSize:11,color:'var(--text-faint)',marginTop:1}}>გადახდილი: ₾{parseFloat(f.paid).toFixed(2)}</div>
                  </div>
                  <span style={{fontSize:14,fontWeight:600,color:'var(--danger)'}}>₾{parseFloat(f.total).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
